// Per-page browser-tab titles (#818). Every public page used to keep the
// boot title (the league name), so a fan's tabs, history and shared links
// all read the same. Pages now title themselves "<Page> · <League>" in the
// admin's pattern: a section page uses its nav label, a record page the
// record's own name once loaded (its section's label until then). Home
// stays the bare league name. Template-independent: set once in the page
// binding (PageRoute), never in a template. The member and registration
// pages (#1463) title themselves too: a registration or an invitation by its
// own heading once loaded, the rest by a fixed name (pages.memberTitles).

import { useEffect } from 'react'
import { useLeague } from '@/contexts/LeagueContext'
import { messages, useLocale } from '@/i18n'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'
import type pagesEn from '@/i18n/catalogs/site-core/pages/en'
import type { StaticNavKey } from '../chrome/useSiteNav'
import type { PageVMMap, SitePageKey } from './pages'

/** Section pages, and the section a record page falls back to while it loads: a `pages.nav` label. */
const SECTION_TITLE: Partial<Record<SitePageKey, StaticNavKey>> = {
  schedule: 'schedule',
  game: 'schedule',
  players: 'players',
  player: 'players',
  teams: 'teams',
  team: 'teams',
  stats: 'stats',
  gallery: 'gallery',
  blog: 'blog',
  venues: 'venues',
  venue: 'venues',
  liveEvents: 'live',
  liveEvent: 'live',
  tickets: 'tickets',
  sale: 'tickets',
}

/** The member and registration pages: their fixed title, a `pages.memberTitles` key. */
const MEMBER_TITLE: Partial<Record<SitePageKey, keyof (typeof pagesEn)['memberTitles']>> = {
  signIn: 'signIn',
  registrations: 'registrations',
  registration: 'registration',
  account: 'account',
  forgotPassword: 'forgotPassword',
  resetPassword: 'resetPassword',
  join: 'join',
  membersOnly: 'membersOnly',
}

type AnyVM = PageVMMap[SitePageKey] & Record<string, unknown>
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null)

/** The record's own name, when the page is a record page and it has loaded. */
function recordTitle(page: SitePageKey, vm: AnyVM): string | null {
  const o = (k: string) => (vm[k] && typeof vm[k] === 'object' ? (vm[k] as Record<string, unknown>) : null)
  switch (page) {
    case 'team': return str(o('team')?.name)
    case 'player': {
      const p = o('player')
      const name = [str(p?.first_name), str(p?.last_name)].filter(Boolean).join(' ')
      return name || null
    }
    case 'game': {
      const h = o('header')
      const home = str((h?.home as Record<string, unknown> | undefined)?.name)
      const away = str((h?.away as Record<string, unknown> | undefined)?.name)
      return home && away ? `${away} vs ${home}` : null
    }
    case 'sponsor': return str(o('sponsor')?.name)
    case 'sale': return str(o('sale')?.title)
    case 'page': return str(o('page')?.title)
    case 'liveEvent': return str(o('event')?.title)
    case 'venue': return str(o('venue')?.name)
    // The page's own h1: the registration's name, or "Join Harbor Kings".
    case 'registration': return str(vm.title)
    case 'join': return str(vm.title)
    default: return null
  }
}

/** "<part> · <league>", or just the league for Home / an unknown part. */
export function pageTitle(page: SitePageKey, vm: unknown, leagueName: string): string {
  const section = SECTION_TITLE[page]
  const member = MEMBER_TITLE[page]
  const pages = messages(siteCoreMessages).pages
  const part =
    (vm && typeof vm === 'object' ? recordTitle(page, vm as AnyVM) : null) ??
    (section ? pages.nav[section] : member ? pages.memberTitles[member] : null)
  return part && leagueName ? `${part} · ${leagueName}` : part || leagueName
}

// The league, or null outside a LeagueProvider (isolated page tests).
function useLeagueOrNull() {
  try {
    return useLeague().league || null
  } catch {
    return null
  }
}

/**
 * Marker on <html> while a page owns the title, so LeagueContext's boot
 * metadata (which runs again whenever the tenant resolves or refetches)
 * doesn't put the bare league name back over it.
 */
export const PAGE_TITLE_ATTR = 'data-page-title'

export function usePageTitle(page: SitePageKey, vm: unknown): void {
  const league = useLeagueOrNull()
  useLocale() // the section titles follow the language
  const name = league?.name || ''
  const title = pageTitle(page, vm, name)
  useEffect(() => {
    if (!title) return
    const html = document.documentElement
    document.title = title
    html.setAttribute(PAGE_TITLE_ATTR, '')
    return () => {
      // Leaving a titled page (e.g. for the not-found route): back to the league.
      html.removeAttribute(PAGE_TITLE_ATTR)
      if (name) document.title = name
    }
    // `league` re-asserts the title after a tenant refetch rewrote it.
  }, [title, name, league])
}
